import React, { FC, useCallback, useEffect } from 'react';
import { Alert, Field, Select, Toggle, Tooltip } from '@kontentino/ui';
import { PostComposerFormState } from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import { UseSetStateAction } from 'utils/hooks/useSetState';
import { TiktokMeta } from 'types/Attachments';
import useBoolean from 'utils/hooks/useBoolean';
import Link from 'components/shared/Link';
import { PseudoPostType } from 'constants/post';
import PostsConfig from 'app/config/posts';

type Props = {
  value: PostComposerFormState;
  onChange: UseSetStateAction<PostComposerFormState>;
};

const initialTiktokMeta: TiktokMeta = {
  privacy_level: null,
  disable_comment: false,
  disable_duet: false,
  disable_stitch: false,
  brand_content_toggle: false,
  brand_organic_toggle: false,
};

export const PrivacyLevel = {
  PUBLIC_TO_EVERYONE: 'PUBLIC_TO_EVERYONE',
  MUTUAL_FOLLOW_FRIENDS: 'MUTUAL_FOLLOW_FRIENDS',
  FOLLOWER_OF_CREATOR: 'FOLLOWER_OF_CREATOR',
  SELF_ONLY: 'SELF_ONLY',
};

const PostComposerContentTikTokMeta: FC<Props> = ({
  value: { attachments, pseudoType },
  onChange,
}) => {
  const [discloseContent, setDiscloseContent] = useBoolean(
    attachments.tiktokMeta?.brand_content_toggle ||
      attachments.tiktokMeta?.brand_organic_toggle,
  );

  const onTiktokMetaChange = useCallback(
    (changes: Partial<TiktokMeta>) => {
      onChange((prev) => ({
        ...prev,
        attachments: {
          ...prev.attachments,
          tiktokMeta: {
            ...initialTiktokMeta,
            ...prev.attachments.tiktokMeta,
            ...changes,
          },
        },
      }));
    },
    [onChange],
  );

  useEffect(() => {
    onTiktokMetaChange({});
  }, [onTiktokMetaChange]);

  const contentTypeLabel =
    pseudoType === PseudoPostType.Video ? 'video' : 'photo';

  const privacyLevelOptions = [
    {
      value: PrivacyLevel.PUBLIC_TO_EVERYONE,
      label: 'Public to everyone',
      disabled: false,
    },
    {
      value: PrivacyLevel.MUTUAL_FOLLOW_FRIENDS,
      label: 'Mutual follow friends',
      disabled: false,
    },
    {
      value: PrivacyLevel.FOLLOWER_OF_CREATOR,
      label: 'Follower of creator',
      disabled: false,
    },
    {
      value: PrivacyLevel.SELF_ONLY,
      label: 'Self only',
      disabled: !!attachments.tiktokMeta?.brand_content_toggle,
    },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {(PostsConfig.TIKTOK_ALLOW_PRIVACY_LEVEL_FOR_VIDEOS &&
        pseudoType === PseudoPostType.Video) ||
        (pseudoType !== PseudoPostType.Video && (
          <Field.Group>
            <Field.Label required>
              Who can view this {contentTypeLabel}
            </Field.Label>
            <Select
              isCreatable={false}
              options={privacyLevelOptions}
              isClearable
              isOptionDisabled={(option) => option.disabled}
              defaultValue={privacyLevelOptions[0]}
              onChange={(value) =>
                onTiktokMetaChange({
                  privacy_level: value?.value || null,
                })
              }
              value={
                attachments.tiktokMeta?.privacy_level
                  ? privacyLevelOptions.find(
                      (option) =>
                        option.value === attachments.tiktokMeta?.privacy_level,
                    )
                  : null
              }
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{
                menuPortal: (styles) => ({
                  ...styles,
                  zIndex: 1010,
                }),
              }}
              data-name="ai-content-language-select"
            />
          </Field.Group>
        ))}
      <Field.Group>
        <Field.Label required>Allow users to</Field.Label>
        <div className="tw-flex tw-gap-4">
          <Field.Group>
            <div className="tw-flex tw-items-center">
              <Field.Label
                required
                className="tw-flex tw-items-center tw-gap-2"
              >
                <Toggle
                  checked={!attachments.tiktokMeta?.disable_comment}
                  onChange={() =>
                    onTiktokMetaChange({
                      disable_comment: !attachments.tiktokMeta?.disable_comment,
                    })
                  }
                />
                <span>Comment</span>
              </Field.Label>
            </div>
          </Field.Group>
          {pseudoType === PseudoPostType.Video && (
            <>
              <Field.Group>
                <div className="tw-flex tw-items-center">
                  <Field.Label
                    required
                    className="tw-flex tw-items-center tw-gap-2"
                  >
                    <Toggle
                      checked={!attachments.tiktokMeta?.disable_duet}
                      onChange={() =>
                        onTiktokMetaChange({
                          disable_duet: !attachments.tiktokMeta?.disable_duet,
                        })
                      }
                    />
                    <span>Duet</span>
                  </Field.Label>
                </div>
              </Field.Group>
              <Field.Group>
                <div className="tw-flex tw-items-center">
                  <Field.Label
                    required
                    className="tw-flex tw-items-center tw-gap-2"
                  >
                    <Toggle
                      checked={!attachments.tiktokMeta?.disable_stitch}
                      onChange={() =>
                        onTiktokMetaChange({
                          disable_stitch:
                            !attachments.tiktokMeta?.disable_stitch,
                        })
                      }
                    />
                    <span>Stitch</span>
                  </Field.Label>
                </div>
              </Field.Group>
            </>
          )}
        </div>
      </Field.Group>
      <Field.Group>
        <Field.Label required className="tw-flex tw-items-center tw-gap-2">
          Disclose {contentTypeLabel} content
          <Toggle
            checked={discloseContent}
            onChange={() => {
              setDiscloseContent.toggle();
              if (discloseContent) {
                onTiktokMetaChange({
                  brand_content_toggle: false,
                  brand_organic_toggle: false,
                });
              }
            }}
          />
        </Field.Label>
        {discloseContent && (
          <Alert>
            Your {contentTypeLabel} will be labeled as "
            {attachments.tiktokMeta?.brand_content_toggle
              ? 'Paid partnership'
              : 'Promotional content'}
            ". This cannot be changed once your {contentTypeLabel} is posted.
          </Alert>
        )}
        <span className="tw-text-sm tw-text-grayscale-100">
          Turn on to disclose that this {contentTypeLabel} promotes goods or
          services in exchange for something of value. Your {contentTypeLabel}{' '}
          could promote yourself, a third party, or both.
        </span>
      </Field.Group>
      {discloseContent && (
        <>
          <Field.Group>
            <Field.Label required className="tw-flex tw-items-center tw-gap-2">
              Your brand
              <Toggle
                checked={attachments.tiktokMeta?.brand_organic_toggle}
                onChange={() =>
                  onTiktokMetaChange({
                    brand_organic_toggle:
                      !attachments.tiktokMeta?.brand_organic_toggle,
                  })
                }
              />
            </Field.Label>
            <span className="tw-text-sm tw-text-grayscale-100">
              You are promoting yourself or your own business. This{' '}
              {contentTypeLabel} will be classified as Brand Organic.
            </span>
          </Field.Group>
          <Field.Group>
            <Tooltip
              placement="left"
              content={
                attachments.tiktokMeta?.privacy_level === PrivacyLevel.SELF_ONLY
                  ? 'Branded content visibility cannot be set to private.'
                  : undefined
              }
            >
              <Field.Label
                required
                className="tw-flex tw-items-center tw-gap-2"
              >
                Branded content
                <Toggle
                  disabled={
                    attachments.tiktokMeta?.privacy_level ===
                    PrivacyLevel.SELF_ONLY
                  }
                  checked={attachments.tiktokMeta?.brand_content_toggle}
                  onChange={() =>
                    onTiktokMetaChange({
                      brand_content_toggle:
                        !attachments.tiktokMeta?.brand_content_toggle,
                    })
                  }
                />
              </Field.Label>
            </Tooltip>
            <span className="tw-text-sm tw-text-grayscale-100">
              You are promoting another brand or a third party. This{' '}
              {contentTypeLabel} will be classified as Branded Content.
            </span>
          </Field.Group>
        </>
      )}
      <Field.Group>
        <span className="tw-text-sm tw-text-grayscale-100">
          By posting, you agree to TikTok's{' '}
          {attachments.tiktokMeta?.brand_content_toggle && (
            <span>
              <Link
                to="https://www.tiktok.com/legal/page/global/bc-policy/en"
                className="tw-text-primary-90"
                target="_blank"
              >
                Branded Content Policy
              </Link>
              &nbsp;and&nbsp;
            </span>
          )}
          <Link
            to="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
            className="tw-text-primary-90"
            target="_blank"
          >
            Music Usage Confirmation
          </Link>
          .
        </span>
      </Field.Group>
    </div>
  );
};

export default PostComposerContentTikTokMeta;
